/**
 * Shared palette + ordering for bug-report tags. Single source of truth
 * for both the compose form (QuickReportOverlay) and the list views
 * (MyReportsPage, DevDashboardPage, DevReportsPage, ReportThread).
 *
 * 2026-04-30: `feature` was renamed to `feedback`. Old reports may still
 * carry the `feature` tag — `normalizeTag` maps them at read time so the
 * UI displays them consistently without a destructive backfill.
 *
 * Color choices:
 *   - critical:   red       — severity, always urgent
 *   - bug:        yellow    — broken behavior, needs investigation
 *   - interface:  purple    — UI/layout polish
 *   - suggestion: pastel blue   — new ideas, lower urgency
 *   - feedback:   pastel pink   — preference / opinion
 *   - question:   pastel purple — needs answering, not fixing
 *
 * CATEGORY_ORDER drives the "all" view's grouping: critical → bugs →
 * interface → suggestion → question → feedback. Higher-urgency
 * categories sort first.
 */

export const TAG_COLORS = {
  critical:   '#ff3b30',
  bug:        '#f5c842',
  interface:  '#9b59b6',
  ui:         '#9b59b6', // legacy alias for interface
  suggestion: '#7eb6e8',
  question:   '#b89bdb',
  feedback:   '#e8a5c4',
  feature:    '#e8a5c4', // legacy: feature reads as feedback
};

// Status fill palette — used to tint cards based on lifecycle.
// Distinct hues so the eye separates them at a glance:
//   - addressed (dev's soft-resolve, awaiting confirmation): soft
//     blue-green leaning green. The "hopeful" stage — still in flight.
//   - resolved: a deeper, more saturated forest green. Done done.
//   - active threads (a special case set by the reporter card when
//     `commentCount > 0`): light blue tint. Distinct from the green
//     family so a thread doesn't look like an addressed report.
export const STATUS_FILL = {
  open:           'transparent',
  addressed:      'rgba(80, 200, 160, 0.16)',
  resolved:       'rgba(40, 160, 70, 0.32)',
  threadOnOpen:   'rgba(74, 144, 217, 0.14)',
};

export const STATUS_BORDER = {
  open:      '#ff6961',
  addressed: '#50c8a0',
  resolved:  '#28a046',
};

export const STATUS_LABEL = {
  open:      'Open',
  addressed: 'Addressed',
  resolved:  'Resolved',
};

// Sort priority for "All" view. Lower index = higher in list.
export const CATEGORY_ORDER = ['critical', 'bug', 'interface', 'suggestion', 'question', 'feedback'];

// Returns the highest-priority category found in a report's tag list.
// Used to bucket reports under their primary category in the all view.
export const primaryCategory = (tags) => {
  if (!Array.isArray(tags) || tags.length === 0) return 'other';
  const normalized = tags.map(normalizeTag);
  for (const cat of CATEGORY_ORDER) {
    if (normalized.includes(cat)) return cat;
  }
  return 'other';
};

// Read-time map for legacy `feature` tag.
export const normalizeTag = (tag) => (tag === 'feature' ? 'feedback' : tag);

export const normalizeTags = (tags) =>
  Array.isArray(tags) ? tags.map(normalizeTag) : [];
